/**
 * The along-the-track model: where the objects of a track lie on its km axis, read from the
 * rows dview:AlongTrackViewer names. Positions are metres along the line (lps02); a position
 * table row gives each piece of an object on this track (an object over several tracks or
 * segments has several pieces, and from can be larger than to).
 */

import type { TableValues } from '@services/entityView.service';
import { conceptOf, type Concept, type ConceptIndex } from '@services/vocabulary.service';
import { setting, settingList, type ViewPropertyShape, type ViewShapes } from '@utils/viewShapes.utils';
import type { EntityObject } from '@/types';

export interface Piece {
  item: string;
  from: number;
  to: number;
}

export type LaneKind = 'step' | 'bars' | 'profile' | 'curves' | 'bands';

export interface Segment extends Piece {
  value: number | null;
  /** Category: the speed profile of a speed section, the system of a power section, the curve type. */
  category: string | null;
  /** The category's role from dview:categories: basis / plus / tilting, arc / transition / straight. */
  role: string | null;
}

export interface Lane {
  key: string;
  row: ViewPropertyShape;
  kind: LaneKind;
  unit: string | undefined;
  segments: Segment[];
  gaps: Piece[];
  /** Items on the track whose position is unknown. */
  unplaced: number;
}

export interface Marker extends Piece {
  /** The concept its number stands for (dview:scheme on the column), for its name. */
  concept?: Concept | null;
  key: string;
  row: ViewPropertyShape;
  symbol: string;
  /** Signal number from the type designator, such as 8.10 for TJN §8-10. */
  number: string | null;
}

/** A "value=role" map from a setting (dview:categories, dview:symbolFor): a value or an IRI's local name to its role. */
export function roleMap(row: ViewPropertyShape, name: string): (value: string | null | undefined) => string | null {
  const map = new Map((row.settings[name] ?? []).map((entry) => entry.split('=') as [string, string]));
  return (value) => (value ? (map.get(value) ?? map.get(localName(value)) ?? null) : null);
}

export interface LinearLocation {
  from: number;
  to: number;
  lanes: Lane[];
  markers: Marker[];
  /** Metres of the stretch where at least one lane has no data. */
  gapLength: number;
  gapCount: number;
}

export interface LinearLocationRows {
  rows: Record<string, ViewPropertyShape>;
  /** The shapes, for a table's column rows (their settings, such as dview:symbolFor). */
  shapes?: ViewShapes | null;
  /** Concepts by scheme and notation, for a column with dview:scheme (the picture on the concept). */
  concepts?: ConceptIndex;
  tables: Record<string, TableValues>;
  values: Record<string, EntityObject[]>;
}

const MIN_GAP = 1;
const LANE_KINDS: LaneKind[] = ['step', 'bars', 'profile', 'curves', 'bands'];

export const num = (values: EntityObject[] | undefined): number | null => {
  const value = values?.[0]?.value;
  const parsed = value === undefined ? NaN : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** 18.428 for 18428 m: km with three decimals, as the km posts read. */
export function formatKm(metres: number): string {
  return (Math.round(metres) / 1000).toFixed(3);
}

/** Every piece of every object, from the position table (columns item, from, to, at). */
export function placePieces(positions: TableValues | undefined): Map<string, Piece[]> {
  const byItem = new Map<string, Piece[]>();
  for (const position of positions?.items ?? []) {
    const from = num(position.cells.from);
    const to = num(position.cells.to);
    const at = num(position.cells.at);
    const range =
      from !== null && to !== null
        ? [Math.min(from, to), Math.max(from, to)]
        : at !== null
          ? [at, at]
          : null;
    if (!range) continue;
    for (const item of position.cells.item ?? []) {
      if (item.type !== 'uri') continue;
      const pieces = byItem.get(item.value) ?? [];
      if (!pieces.some((piece) => piece.from === range[0] && piece.to === range[1])) {
        pieces.push({ item: item.value, from: range[0], to: range[1] });
      }
      byItem.set(item.value, pieces);
    }
  }
  return byItem;
}

/** Stretches of from..to that no piece covers. */
export function findGaps(pieces: Piece[], from: number, to: number): Piece[] {
  const sorted = pieces
    .map((piece) => ({ from: Math.max(piece.from, from), to: Math.min(piece.to, to) }))
    .filter((piece) => piece.to > piece.from)
    .sort((a, b) => a.from - b.from);
  const gaps: Piece[] = [];
  let cursor = from;
  for (const piece of sorted) {
    if (piece.from - cursor > MIN_GAP) gaps.push({ item: '', from: cursor, to: piece.from });
    cursor = Math.max(cursor, piece.to);
  }
  if (to - cursor > MIN_GAP) gaps.push({ item: '', from: cursor, to });
  return gaps;
}

export const localName = (iri: string) => iri.split(/[#/]/).pop() ?? iri;

/** camelCase as words: raisedPantographsNumber → raised Pantographs Number. */
export const words = (name: string) => name.replace(/([a-z])([A-Z])/g, '$1 $2');

/** The track's stretch with its lanes and markers, or null when nothing is positioned on it. */
export function buildLinearLocation(viewer: ViewPropertyShape, data: LinearLocationRows): LinearLocation | null {
  const pieces = placePieces(data.tables[viewer.id]);
  const all = Array.from(pieces.values()).flat();
  const kmRow = (name: string) => {
    const key = setting(viewer, name);
    const row = key ? data.rows[key] : undefined;
    return row ? num(data.values[row.id]) : null;
  };
  const ends = [kmRow('kmFrom'), kmRow('kmTo')].filter((value): value is number => value !== null);
  const from = ends.length === 2 ? Math.min(...ends) : Math.min(...all.map((piece) => piece.from));
  const to = ends.length === 2 ? Math.max(...ends) : Math.max(...all.map((piece) => piece.to));
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;

  const lanes: Lane[] = [];
  for (const key of settingList(viewer, 'lanes')) {
    const row = data.rows[key];
    const table = row ? data.tables[row.id] : undefined;
    const kind = row ? (setting(row, 'lane') as LaneKind) : undefined;
    if (!row || !kind || !LANE_KINDS.includes(kind)) continue;
    const valueKey = setting(row, 'value');
    const categoryKey = setting(row, 'series') ?? (kind === 'bands' ? valueKey : undefined);
    const roleOf = roleMap(row, 'categories');
    const segments: Segment[] = [];
    let unplaced = 0;
    for (const item of table?.items ?? []) {
      const itemPieces = pieces.get(item.iri);
      if (!itemPieces) {
        unplaced++;
        continue;
      }
      const category = categoryKey ? item.cells[categoryKey]?.[0]?.value ?? null : null;
      for (const piece of itemPieces) {
        segments.push({
          ...piece,
          value: valueKey ? num(item.cells[valueKey]) : null,
          category,
          role: roleOf(category),
        });
      }
    }
    segments.sort((a, b) => a.from - b.from);
    lanes.push({
      key,
      row,
      kind,
      unit: setting(row, 'unit'),
      segments,
      gaps: findGaps(segments, from, to),
      unplaced,
    });
  }

  const markers: Marker[] = [];
  for (const key of settingList(viewer, 'objects')) {
    const row = data.rows[key];
    if (!row) continue;
    const table = data.tables[row.id];
    // dview:symbolBy names the column whose value picks a symbol from dview:symbolFor (e.g. main signals)
    const by = setting(row, 'symbolBy');
    // The map sits on the column row (shared with the object's own page), else on this row
    const column = by && row.nodeShapeId ? data.shapes?.nodeShapesById.get(row.nodeShapeId)?.properties.find((property) => property.key === by) : undefined;
    const mapped = roleMap(column ?? row, 'symbolFor');
    const scheme = setting(column ?? row, 'scheme');
    const symbolOf = (value: string | null) => conceptOf(data.concepts, scheme, value)?.symbol ?? mapped(value);
    const items = table
      ? table.items.map((item) => ({ iri: item.iri, number: by ? (item.cells[by]?.[0]?.value ?? null) : null }))
      : (data.values[row.id] ?? []).filter((value) => value.type === 'uri').map((value) => ({ iri: value.value, number: null }));
    for (const item of items) {
      for (const piece of pieces.get(item.iri) ?? []) {
        markers.push({
          ...piece,
          key,
          row,
          symbol: symbolOf(item.number) ?? setting(row, 'symbol') ?? 'point',
          number: item.number,
          concept: conceptOf(data.concepts, scheme, item.number),
        });
      }
    }
  }
  markers.sort((a, b) => a.from - b.from);

  const gaps = lanes.flatMap((lane) => lane.gaps);
  const covered = findGaps(gaps, from, to);
  const covers = covered.reduce((sum, piece) => sum + piece.to - piece.from, 0);
  return {
    from,
    to,
    lanes,
    markers,
    gapLength: gaps.length > 0 ? to - from - covers : 0,
    gapCount: gaps.length,
  };
}

/** The segments of a lane that cover a km. */
export function segmentsAt(lane: Lane, km: number): Segment[] {
  return lane.segments.filter((segment) => segment.from <= km && km <= segment.to);
}

/** The marker nearest to a km, within `tolerance` metres. */
export function nearestMarker(markers: Marker[], km: number, tolerance: number): Marker | null {
  let best: Marker | null = null;
  let bestDistance = tolerance;
  for (const marker of markers) {
    const distance = km < marker.from ? marker.from - km : km > marker.to ? km - marker.to : 0;
    if (distance <= bestDistance) {
      best = marker;
      bestDistance = distance;
    }
  }
  return best;
}

/** Share of the stretch a lane covers, 0..1. */
export function coverage(lane: Lane, stretch: Pick<LinearLocation, 'from' | 'to'>): number {
  const length = stretch.to - stretch.from;
  const missing = lane.gaps.reduce((sum, gap) => sum + gap.to - gap.from, 0);
  return length > 0 ? Math.max(0, 1 - missing / length) : 0;
}
